import { Modal } from "react-bootstrap";
import { LucideIcon, IconNames } from "../../icon/LucideIcon";
import styles from "./AlertModal.module.css";
import { CSSProperties, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button } from "../../buttons/Button/Button";
import { InputFormField } from "../../formfields/InputFormField";
import { PasswordFormField } from "../../formfields/PasswordFormField";
import { useForm } from "react-hook-form";
import { SessionContext } from "../../contexts/SessionContext";
import { LoginRequest } from "../../../api/Login";
import { zIndex } from "../../../api/CommonConstants";

export interface AlertModalOnProceedProps {
  credentials?: { login?: string; password?: string };
}
interface AlertModalProps {
  showModal: boolean;
  setShowModal: (value: React.SetStateAction<boolean>) => void;
  title?: string | JSX.Element;
  description?: string | JSX.Element;
  proceedLabel?: string;
  proceedDisabled?: boolean;
  cancelLabel?: string;
  onCancel?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onProceed: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLInputElement>,
    props?: AlertModalOnProceedProps
  ) => void;
  loading?: boolean; // Flag to indicate loading -> Disables buttons
  type?: "warning" | "danger" | "success" | "primary";
  forceCountdown?: boolean;
  forceUserInput?: boolean;
  forceUserInputText?: string;
  forceUserAuthentication?: boolean;
  children?: React.ReactNode;
  backdropClassName?: string;
  modalStyle?: CSSProperties;
  hideCloseButton?: boolean;
  iconOverride?: IconNames;
}
/**
 * AlertModal component displays a modal with customizable options for user confirmation.
 * @author @CorradoSurmanowicz
 * @param {boolean} showModal - Determines whether the modal is visible.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setShowModal - Function to toggle the visibility of the modal.
 * @param {string} title - The title displayed in the modal.
 * @param {string} description - The description or message displayed in the modal.
 * @param {string} [proceedLabel="Delete"] - Label for the proceed button.
 * @param {boolean} [proceedDisabled=false] - If true, disables the proceed button.
 * @param {string} [cancelLabel="Cancel"] - Label for the cancel button.
 * @param {() => void} [onCancel] - Callback function triggered when the cancel button is clicked.
 * @param {() => void} [onProceed] - Callback function triggered when the proceed button is clicked.
 * @param {boolean} [loading] - Indicates whether the modal is in a loading state.
 * @param {"primary" | "danger" | "success" | "warning"} [type="danger"] - Type of modal, which determines its styling.
 * @param {boolean} [forceCountdown=false] - If true, disables the proceed button for a countdown period.
 * @param {boolean} [forceUserInput=false] - If true, requires user input to enable the proceed button.
 * @param {string} [forceUserInputText="DELETE"] - The text the user must input to enable the proceed button.
 * @param {boolean} [forceUserAuthentication=false] - If true, it adds a form element for login/password that will be added to the request.
 * @param {React.ReactNode} [children] - Optional children to be rendered inside the modal.
 * @param {string} [backdropClassName] - Additional class name for the modal backdrop.
 * @param {React.CSSProperties} [modalStyle] - Inline styles for the modal.
 * @param {boolean} [hideCloseButton=false] - If true, hides the close button in the modal footer.
 *
 * @returns {JSX.Element} The rendered AlertModal component.
 */
export const AlertModal = ({
  showModal,
  setShowModal,
  title,
  description,
  proceedLabel = "Delete",
  proceedDisabled = false,
  cancelLabel = "Cancel",
  onCancel,
  onProceed,
  loading,
  type = "danger",
  forceCountdown = false,
  forceUserInput = false,
  forceUserInputText = "DELETE",
  forceUserAuthentication = false,
  children,
  backdropClassName,
  modalStyle,
  hideCloseButton = false,
  iconOverride,
}: AlertModalProps) => {
  const { api } = useContext(SessionContext);
  const disableOTPmilliseconds = 5000;
  const [isDisabled, setIsDisabled] = useState(false);
  const [disableTimer, setDisableTimer] = useState(disableOTPmilliseconds / 1000);
  const interval = useRef<NodeJS.Timeout>();
  const timeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setIsDisabled(forceCountdown ? true : forceUserInput ? true : false);
    if (forceCountdown) {
      if (showModal) {
        interval.current = setInterval(() => {
          return setDisableTimer((prev) => {
            if (prev > 0) {
              return prev - 1;
            } else {
              return 0;
            }
          });
        }, 1000);
        timeout.current = setTimeout(
          () => {
            setIsDisabled(false);
            setDisableTimer(disableOTPmilliseconds / 1000);
            interval.current && clearInterval(interval.current);
          },

          5000
        );
      } else {
        interval.current && clearInterval(interval.current);
        timeout.current && clearInterval(timeout.current);
        setIsDisabled(true);
        setDisableTimer(disableOTPmilliseconds / 1000);
      }
    }
    return () => {
      interval.current && clearInterval(interval.current);
      timeout.current && clearInterval(timeout.current);
    };
  }, [forceCountdown, forceUserInput, showModal]);

  const validateUserInput = (input: string) => {
    if (input === forceUserInputText) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  };

  const switchType = (_type: typeof type): { headerCls: string; icon?: IconNames; iconCls: string } => {
    switch (_type) {
      case "primary":
        return { headerCls: styles.primary, icon: "circle-help", iconCls: styles.primary_svg };
      case "danger":
        return { headerCls: styles.danger, icon: "triangle-alert", iconCls: styles.danger_svg };
      case "success":
        return { headerCls: styles.success, icon: "check", iconCls: styles.success_svg };
      case "warning":
        return { headerCls: styles.warning, icon: "circle-alert", iconCls: styles.warning_svg };
      default:
        return { headerCls: "", icon: undefined, iconCls: "" };
    }
  };

  const { headerCls, icon: defaultIcon, iconCls } = switchType(type);
  const icon = iconOverride ?? defaultIcon;

  const {
    register,
    formState: { errors },
    watch,
    setError,
    clearErrors,
  } = useForm<Partial<{ login: string; password: string }>>({});

  const currentLogin = watch("login");
  const currentPassword = watch("password");

  useEffect(() => {
    clearErrors("login");
    clearErrors("password");
  }, [clearErrors, currentLogin, currentPassword]);

  const onProceedCallback = useCallback(
    async (e) => {
      if (forceUserAuthentication) {
        try {
          const loginValidated = (await api.post(
            "login/validateLoginData",
            { username: currentLogin, password: currentPassword } as LoginRequest,
            {}
          )) as boolean;
          if (!loginValidated) {
            setError("login", { type: "validate", message: " " });
            setError("password", { type: "validate", message: "Invalid login or password" });
            return;
          }
        } catch (error) {
          console.error("Error validating login data:", error);
          return;
        }
      }

      onProceed(e, {
        ...(forceUserAuthentication ? { credentials: { login: currentLogin, password: currentPassword } } : {}),
      });
    },
    [api, currentLogin, currentPassword, forceUserAuthentication, onProceed, setError]
  );

  const disabled = isDisabled || proceedDisabled || (forceUserAuthentication && (!currentLogin || !currentPassword));

  return (
    <Modal
      show={showModal}
      onHide={() => {
        setShowModal(() => false);
      }}
      backdropClassName={backdropClassName}
      style={{ ...modalStyle, zIndex: zIndex["zIndexBackdrop"] }}
    >
      <Modal.Header className={`${styles.modal_header} ${headerCls}`}>
        <Modal.Title className={styles.modal_title}></Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className={styles.modal_body}>
          <div className={styles.modal_body_icon}>{icon && <LucideIcon name={icon} className={iconCls} />}</div>
          <div className={styles.modal_body_content}>
            <div className={styles.modal_body_content_title}>{title}</div>
            <div className={styles.modal_body_content_description}>{description}</div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex col-nowrap gap-5">
          {forceUserInput && (
            <div className="form-group" style={{ margin: 0 }}>
              <div className="flex row-nowrap align-center gap-5">
                <label className="control-label" style={{ whiteSpace: "nowrap", margin: 0 }}>
                  Confirm action:
                </label>
                <input
                  className="form-control input-sm"
                  type="text"
                  placeholder={`Type: "${forceUserInputText}" to proceed`}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !disabled) {
                      onProceed(e);
                    }
                  }}
                  onChange={(e) => validateUserInput(e.target.value)}
                />
              </div>
            </div>
          )}

          <div>
            {forceUserAuthentication && (
              <form
                className={`form-horizontal ${styles.form_holder}`}
                style={{ margin: 0, padding: "0 10px", border: "none", boxShadow: "none" }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !disabled) {
                    e.preventDefault();
                    onProceedCallback(e);
                  }
                }}
              >
                <InputFormField
                  id="login"
                  label="Login"
                  errors={errors}
                  register={register}
                  autoFocus={true}
                  required={true}
                  horizontal
                  placeholder={"Enter your login..."}
                />

                <PasswordFormField
                  id="password"
                  label="Password"
                  errors={errors}
                  register={register}
                  required={true}
                  horizontal
                />
              </form>
            )}

            {!!children && children}
          </div>

          <div className={styles.modal_footer}>
            {!hideCloseButton && (
              <button
                className="btn btn-soft-secondary"
                onClick={(e) => {
                  onCancel?.(e);
                  setShowModal(false);
                }}
                disabled={loading}
              >
                {cancelLabel}
              </button>
            )}

            <Button className={`btn btn-${type}`} onClick={onProceedCallback} disabled={disabled} loading={loading}>
              {disabled && forceCountdown && `(${disableTimer})`} {proceedLabel}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
